import React, { useEffect, useState } from "react";
import { Button, Form, Row } from "reactstrap";

import { useTranslation } from "react-i18next";
import { GetLanguageString } from "../../../helper/Components";
import { ApiFinance } from "../../ApiFinance";
import { GetCustomStyles } from "../../../helper/Method";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { IoArrowBackSharp } from "react-icons/io5";
import swal from "sweetalert";

import { encryptStorage } from "../../../../constants/EncryptStorage";
import { LoadingSpinner } from "../../../../constants/const";
import CashToAmanatForm from "./CashToAmanatForm";
import { validateTransactionDate } from "../../fiscalYearHelper";
import { parseBalanceNumber } from "../../BalanceCards";
const TransferCashinHandtoAmanat = ({ history }) => {
  const { t } = useTranslation();

  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [fundAccountBalances, setFundAccountBalances] = useState([]);
  const [loading, setLoading] = useState(false);

  // -- FORM DATA --
  const [values, setValues] = useState({
    transferAmount: null,
    bankCharge: null,
    memo: "",
    referenceNo: null,
    depositDate: new Date(),
    file: null,
  });

  const [errors, setErrors] = useState({});

  // FETCH BALANCE
  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetCashToAmanatTransferData(loggedInUnitId)
      .then((res) => setFundAccountBalances(res.data))
      .catch((err) => {
        console.error("Error fetching fund account balances:", err);
        swal({
          title: t("network_requet_error"),
          icon: "error",
          buttons: "OK",
        });
        history.goBack();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [loggedInUnitId, history, t]);

  // INPUT HANDLER
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Enforce numeric-only inputs
    if (name === "transferAmount" || name === "bankCharge") {
      newValue = value.replace(/\D/g, "");
      e.target.value = newValue;
    }

    // Always update values first
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear previous error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Now apply validation only for transferAmount
    if (name === "transferAmount") {
      const transferAmount = Number(newValue) || 0;
      const cashBalance = parseBalanceNumber(
        fundAccountBalances?.cashEffectiveBalance ??
        fundAccountBalances?.cash_effective_balance ??
        fundAccountBalances?.cashAccountBalance
      );

      if (transferAmount > cashBalance) {
        setErrors((prev) => ({
          ...prev,
          transferAmount: t(
            "transfer_amount_greater_than_cash_account_balance"
          ),
        }));

        setValues((prev) => ({
          ...prev,
          file: null,
        }));
      }
    }
  };

  // DATE HANDLER
  const handleDateChange = (date) => {
    setValues({
      ...values,
      depositDate: date,
    });
    setErrors((prev) => ({ ...prev, depositDate: "" }));
  };

  // FILE HANDLER
  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const maxSize = 10 * 1024 * 1024;
    if (uploadedFile.size > maxSize) {
      setErrors({ ...errors, file: "File must be under 10MB" });
      return;
    } else {
      setErrors((prev) => ({ ...prev, file: "" }));
    }

    setValues({ ...values, file: uploadedFile });
  };
  // VALIDATION
  const validate = () => {
    let temp = {};

    temp.transferAmount = "";

    const transferAmount = Number(values.transferAmount || 0);
    const cashBalance = parseBalanceNumber(
      fundAccountBalances?.cashEffectiveBalance ??
      fundAccountBalances?.cash_effective_balance ??
      fundAccountBalances?.cashAccountBalance
    );

    // 1. Required
    if (!values.transferAmount) {
      temp.transferAmount = t("common_field_required_error");

      // 2. Not zero
    } else if (transferAmount === 0) {
      temp.transferAmount = t("common_field_required_error");
    } else if (transferAmount > cashBalance) {
      temp.transferAmount = t(
        "transfer_amount_greater_than_cash_account_balance"
      );
    }
    temp.bankCharge = values.bankCharge ? "" : "";
    const dateError = validateTransactionDate(values.depositDate);
    if (dateError) {
      temp.depositDate = t(dateError);
    } else {
      temp.depositDate = values.depositDate
        ? ""
        : t("common_field_required_error");
    }
    temp.referenceNo = values.referenceNo
      ? ""
      : t("common_field_required_error");

    temp.memo = values.memo && values.memo.trim() !== "" ? "" : t("common_field_required_error");
    temp.file = values.file ? "" : t("common_field_required_error");

    setErrors({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };
  // SUBMIT HANDLER

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("transferAmount", values.transferAmount);
      formData.append("bankCharge", values.bankCharge ? values.bankCharge : 0);

      formData.append("memo", values.memo);
      formData.append("referenceNo", values.referenceNo);
      formData.append("depositDate", values.depositDate.toISOString());
      if (values.file) {
        formData.append("File", values.file);
      }
      formData.append("unitId", loggedInUnitId);

      const res = await ApiFinance().TransferCashinHandtoAmanat(formData);
      if (res.data.statusCode === "200") {
        swal({
          title: t("transfer_request_sent_successfully") || "Transfer request sent successfully",
          icon: "success",
          buttons: "OK",
        });
        history.goBack();
      } else {
        swal({
          title: t("transfer_failed") || "Error transferring fund",
          text: res.data.message || res.data.Msg || res.data.Message || "",
          icon: "error",
          buttons: "OK",
        });
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Msg ||
        error?.response?.data?.Message ||
        (typeof error?.response?.data === "string" ? error?.response?.data : "") ||
        error?.message ||
        "Error transferring fund";
      swal({
        title: t("transfer_failed") || "Error transferring fund",
        text: errorMsg,
        icon: "error",
        buttons: "OK",
      });
    } finally {
      setLoading(false);
      values.file = null;
    }
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title">
          <h5>
            {
              <GetLanguageString props="transactions_cashinhand_to_amanat_account" />
            }
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <CashToAmanatForm
                values={values}
                errors={errors}
                fundAccountBalances={fundAccountBalances}
                handleInputChange={handleInputChange}
                handleDateChange={handleDateChange}
                handleFileChange={handleFileChange}
              />

              {/* Footer */}
              <Row
                style={{
                  backgroundColor: "white",
                  marginLeft: 2,
                  marginRight: 2,
                  height: 50,
                }}
              >
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Button
                    color="white"
                    className={GetCustomStyles().btn_style}
                    onClick={() => history.goBack()}
                  >
                    <GetLanguageString props="common_cancel" />
                  </Button>
                  <Button
                    color="primary"
                    className={GetCustomStyles().btn_style}
                    type="submit"
                  >
                    <GetLanguageString props="transfer" />
                  </Button>
                </div>
              </Row>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(TransferCashinHandtoAmanat);
